import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// Critical configuration to prevent cache persistence
const QUERY_CONFIG = {
  staleTime: 3 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  retry: 1,
  refetchOnWindowFocus: false,
};

// Evaluated at hook-call time (inside the component render cycle),
// not at module-load time, so it correctly reflects the current
// localStorage state after login has stored the token.
const hasToken = () => !!localStorage.getItem('authToken');

/**
 * Clears all auth state and fires a 'tokenExpired' event so the Dashboard
 * can show the warning banner before redirecting to /login.
 */
const handleAuthError = () => {
  // Ignore 401s that arrive within 10 seconds of a fresh login —
  // these are stale in-flight requests that raced with the new token.
  const storedAt = parseInt(localStorage.getItem('authTokenStoredAt') || '0', 10);
  if (Date.now() - storedAt < 10000) return;

  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('is_superuser');
  localStorage.removeItem('authTokenStoredAt');
  window.dispatchEvent(new CustomEvent('tokenExpired'));
};

export const useDashboardStats = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const response = await axios.get(`${API_BASE_URL}/dashboard/stats/`, { headers });
        return response.data;
      } catch (err) {
        if (err.response?.status === 401) handleAuthError();
        throw err;
      }
    },
    ...QUERY_CONFIG,
    enabled: hasToken(),
  });
};

export const useQuotations = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['quotations'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const response = await axios.get(`${API_BASE_URL}/api/quotations/?format=json`, { headers });
        return response.data.results || response.data || [];
      } catch (err) {
        if (err.response?.status === 401) handleAuthError();
        throw err;
      }
    },
    ...QUERY_CONFIG,
    enabled: hasToken(),
  });
};

export const useUsers = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const response = await axios.get(`${API_BASE_URL}/api/users/?format=json`, { headers });
        return response.data.results || response.data || [];
      } catch (err) {
        if (err.response?.status === 401) handleAuthError();
        throw err;
      }
    },
    ...QUERY_CONFIG,
    retry: 1,
    enabled: hasToken(),
  });
};

export const useFollowups = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['followups'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const response = await axios.get(`${API_BASE_URL}/api/followups/?format=json`, { headers });
        return response.data.results || response.data || [];
      } catch (err) {
        if (err.response?.status === 401) handleAuthError();
        throw err;
      }
    },
    ...QUERY_CONFIG,
    enabled: hasToken(),
  });
};

export const useDocuments = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const response = await axios.get(`${API_BASE_URL}/api/documents/?format=json`, { headers });
        return response.data.results || response.data || [];
      } catch (err) {
        if (err.response?.status === 401) handleAuthError();
        throw err;
      }
    },
    ...QUERY_CONFIG,
    enabled: hasToken(),
  });
};

export const useNotifications = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const response = await axios.get(`${API_BASE_URL}/api/notifications/`, { headers });
        return response.data || [];
      } catch (err) {
        if (err.response?.status === 401) handleAuthError();
        throw err;
      }
    },
    ...QUERY_CONFIG,
    enabled: hasToken(),
  });
};

export const useSubscribers = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['subscribers'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const response = await axios.get(`${API_BASE_URL}/newsletter/subscribers/?format=json`, { headers });
        return response.data.results || response.data || [];
      } catch (err) {
        if (err.response?.status === 401) handleAuthError();
        throw err;
      }
    },
    ...QUERY_CONFIG,
    enabled: hasToken(),
  });
};

export const useNewsletterStats = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['newsletterStats'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      try {
        const response = await axios.get(`${API_BASE_URL}/newsletter/stats/`, { headers });
        return response.data;
      } catch (err) {
        if (err.response?.status === 401) handleAuthError();
        throw err;
      }
    },
    ...QUERY_CONFIG,
    enabled: hasToken(),
  });
};

export const useAllDashboardData = (API_BASE_URL, queryClient) => {
  const statsQuery = useDashboardStats(API_BASE_URL);
  const quotationsQuery = useQuotations(API_BASE_URL);
  const usersQuery = useUsers(API_BASE_URL);
  const followupsQuery = useFollowups(API_BASE_URL);
  const documentsQuery = useDocuments(API_BASE_URL);
  const subscribersQuery = useSubscribers(API_BASE_URL);
  const notificationsQuery = useNotifications(API_BASE_URL);
  const newsletterStatsQuery = useNewsletterStats(API_BASE_URL);

  // Derive solar/electrical data from quotations — no separate endpoints exist.
  // service_type is a field on Quotation: 'solar', 'electrical', or 'both'.
  const quotations = quotationsQuery.data || [];
  const solarQuotations = quotations.filter(q => q.service_type === 'solar' || q.service_type === 'both');
  const electricalQuotations = quotations.filter(q => q.service_type === 'electrical' || q.service_type === 'both');
  const electricalServices = electricalQuotations;

  return {
    stats: statsQuery.data || {},
    quotations: quotations,
    users: usersQuery.data || [],
    solarQuotations: solarQuotations,
    electricalQuotations: electricalQuotations,
    electricalServices: electricalServices,
    followups: followupsQuery.data || [],
    documents: documentsQuery.data || [],
    subscribers: subscribersQuery.data || [],
    notifications: notificationsQuery.data || [],
    newsletterStats: newsletterStatsQuery.data || {},
    isPending:
      statsQuery.isPending ||
      quotationsQuery.isPending ||
      usersQuery.isPending ||
      followupsQuery.isPending ||
      documentsQuery.isPending ||
      subscribersQuery.isPending ||
      notificationsQuery.isPending ||
      newsletterStatsQuery.isPending,
    isFetching:
      statsQuery.isFetching ||
      quotationsQuery.isFetching ||
      usersQuery.isFetching ||
      followupsQuery.isFetching ||
      documentsQuery.isFetching ||
      subscribersQuery.isFetching ||
      notificationsQuery.isFetching ||
      newsletterStatsQuery.isFetching,
    isError:
      statsQuery.isError ||
      quotationsQuery.isError ||
      usersQuery.isError ||
      followupsQuery.isError ||
      documentsQuery.isError ||
      subscribersQuery.isError ||
      notificationsQuery.isError ||
      newsletterStatsQuery.isError,
    refetchAll: () => {
      statsQuery.refetch();
      quotationsQuery.refetch();
      usersQuery.refetch();
      followupsQuery.refetch();
      documentsQuery.refetch();
      subscribersQuery.refetch();
      notificationsQuery.refetch();
      newsletterStatsQuery.refetch();
      // Also invalidate the Topbar's user profile query so it re-fetches
      // fresh data from the API rather than serving stale cache
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      }
    }
  };
};